import { OpenImgContextProvider } from 'openimg/react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	data,
	Link,
	Links,
	Meta,
	NavLink,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import osLogoUrl from './assets/favicons/os-logo.svg'
import logoHorizontalUrl from './assets/os-logos/logo-text-horizontal.png'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { Matomo } from './components/matomo.tsx'
import { marketingNavLinks } from './components/marketing-nav-links.ts'
import { MobileMenu } from './components/mobile-menu.tsx'
import { NewsletterSignup } from './components/newsletter-signup.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { Card } from './components/ui/card.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import {
	getLocale,
	i18nextMiddleware,
	localeCookie,
} from './middleware/i18next.ts'
import {
	ThemeSwitch,
	useOptionalTheme,
	useTheme,
} from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { isShopPath, useLocalizedPath } from './utils/i18n.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { trackEvent, useExternalLinkTracking } from './utils/tracking.ts'
import { useOptionalUser } from './utils/user.ts'

export const middleware = [i18nextMiddleware]

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload primary font to avoid FOUT
		{
			rel: 'preload',
			href: '/fonts/manrope-latin.woff2',
			as: 'font',
			type: 'font/woff2',
			crossOrigin: 'anonymous',
		} as const,
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: osLogoUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Open Sourced' : 'Error | Open Sourced' },
		{
			name: 'description',
			content: 'Open source software consulting and development',
		},
	]
}

export async function loader({ request, context }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const locale = getLocale(context)
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			locale,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				{
					'Set-Cookie': await localeCookie.serialize(locale),
				},
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const { i18n, t } = useTranslation()
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html
			lang={i18n.language}
			dir={i18n.dir(i18n.language)}
			className={`${theme} h-full overflow-x-hidden`}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				<a
					href="#main"
					className="focus-cosy focus:bg-background focus:text-foreground sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:rounded-md focus:px-4 focus:py-2"
				>
					{t('a11y.skipToContent')}
				</a>
				{children}
				{env.MATOMO_URL && env.MATOMO_SITE_ID ? (
					<Matomo url={env.MATOMO_URL} siteId={env.MATOMO_SITE_ID} />
				) : null}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const { t, i18n } = useTranslation()
	const user = useOptionalUser()
	const theme = useTheme()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const menuButtonRef = useRef<HTMLButtonElement>(null)
	const localePath = useLocalizedPath()
	const shopActive = isShopPath(useLocation().pathname)
	useToast(data.toast)

	useExternalLinkTracking()

	const navLinks = marketingNavLinks(localePath, t)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="flex min-h-screen flex-col justify-between">
				<header className="fixed top-0 z-50 w-full print:hidden">
					<div className="mx-auto max-w-5xl px-4 pt-4">
						<Card
							variant="glass"
							className="h-14 flex-row items-center justify-between gap-0 py-0"
						>
							<Logo />

							{/* Desktop nav */}
							<nav className="hidden items-center gap-1 md:flex">
								{navLinks.map((link) =>
									link.external ? (
										<a
											key={link.to}
											href={link.to}
											aria-current={shopActive ? 'page' : undefined}
											className={`text-body-xs rounded-xl px-3 py-1.5 transition-colors ${
												shopActive
													? 'bg-accent text-foreground font-semibold'
													: 'text-muted-foreground hover:bg-accent hover:text-foreground'
											}`}
											onClick={() =>
												trackEvent(
													'navigation',
													'click',
													`${link.slug}-desktop`,
												)
											}
										>
											{link.label}
										</a>
									) : (
										<NavLink
											key={link.to}
											to={link.to}
											className={({ isActive }) =>
												`text-body-xs rounded-xl px-3 py-1.5 transition-colors ${
													isActive
														? 'bg-accent text-foreground font-semibold'
														: 'text-muted-foreground hover:bg-accent hover:text-foreground'
												}`
											}
											onClick={() =>
												trackEvent(
													'navigation',
													'click',
													`${link.slug}-desktop`,
												)
											}
										>
											{link.label}
										</NavLink>
									),
								)}
							</nav>

							{/* Right side: theme switch + CTA / user + mobile hamburger */}
							<div className="flex items-center gap-3">
								<div className="hidden md:block">
									<ThemeSwitch
										userPreference={data.requestInfo.userPrefs.theme}
									/>
								</div>
								{user ? (
									<div className="hidden md:block">
										<UserDropdown />
									</div>
								) : (
									<div className="hidden md:block">
										<Button asChild size="sm">
											<Link to={localePath('/contact')}>
												{t('nav.contact')}
											</Link>
										</Button>
									</div>
								)}
								<Button
									ref={menuButtonRef}
									variant="ghost"
									size="icon"
									className="md:hidden"
									onClick={() => setMobileMenuOpen(true)}
									aria-label={t('a11y.openMenu')}
									aria-expanded={mobileMenuOpen}
								>
									<Icon name="hamburger-menu" size="md" />
								</Button>
							</div>
						</Card>
					</div>
				</header>

				<MobileMenu
					isOpen={mobileMenuOpen}
					onClose={() => setMobileMenuOpen(false)}
					returnFocusRef={menuButtonRef}
					user={user ? { name: user.name, username: user.username } : null}
					userPreference={data.requestInfo.userPrefs.theme}
				/>

				<main id="main" className="flex flex-1 flex-col">
					<Outlet />
				</main>

				<footer className="border-border bg-band border-t print:hidden">
					<div className="container py-12">
						{/* Logo — full-width header row */}
						<div className="mb-10">
							<Logo imgClassName="h-16 w-auto dark:invert dark:brightness-90" />
						</div>

						{/* Nav columns + newsletter on the same row */}
						<div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_2fr]">
							{/* Angebote */}
							<div>
								<p className="text-body-xs text-foreground mb-3 font-semibold tracking-wider uppercase">
									{t('footer.offers')}
								</p>
								<ul className="text-body-sm text-muted-foreground space-y-1.5">
									<li>
										<Link
											to={localePath('/websites')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('nav.websites')}
										</Link>
									</li>
									<li>
										<Link
											to={localePath('/coaching')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('nav.coaching')}
										</Link>
									</li>
									<li>
										<Link
											to={localePath('/cloudron')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('nav.cloudron')}
										</Link>
									</li>
									<li>
										<a
											href="/shop"
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('nav.shop')}
										</a>
									</li>
								</ul>
							</div>

							{/* Unternehmen */}
							<div>
								<p className="text-body-xs text-foreground mb-3 font-semibold tracking-wider uppercase">
									{t('footer.company')}
								</p>
								<ul className="text-body-sm text-muted-foreground space-y-1.5">
									<li>
										<Link
											to={localePath('/about')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('nav.about')}
										</Link>
									</li>
									<li>
										<Link
											to={localePath('/blog')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('nav.blog')}
										</Link>
									</li>
									<li>
										<Link
											to={localePath('/contact')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('nav.contact')}
										</Link>
									</li>
								</ul>
							</div>

							{/* Legal */}
							<div>
								<p className="text-body-xs text-foreground mb-3 font-semibold tracking-wider uppercase">
									{t('footer.legal')}
								</p>
								<ul className="text-body-sm text-muted-foreground space-y-1.5">
									<li>
										<Link
											to={localePath('/imprint')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('footer.imprint')}
										</Link>
									</li>
									<li>
										<Link
											to={localePath('/privacy')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('footer.privacy')}
										</Link>
									</li>
									<li>
										<Link
											to={localePath('/terms')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('footer.terms')}
										</Link>
									</li>
									<li>
										<Link
											to={localePath('/dpa')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('footer.dpa')}
										</Link>
									</li>
									<li>
										<Link
											to={localePath('/sla')}
											className="hover:text-foreground inline-block py-0.5 transition-colors"
										>
											{t('footer.sla')}
										</Link>
									</li>
								</ul>
							</div>

							{/* Newsletter — wider column */}
							<Card
								variant="glass"
								className="px-6 py-2 sm:col-span-2 lg:col-span-1"
							>
								<NewsletterSignup compact />
							</Card>
						</div>

						{/* Bottom bar */}
						<div className="border-border text-body-xs text-muted-foreground mt-10 flex flex-col items-center justify-between gap-3 border-t pt-6 sm:flex-row">
							<span>
								{t('footer.copyright', {
									year: new Date().getFullYear(),
								})}
							</span>
							<a
								href={localePath('/blog/feed.xml')}
								className="hover:text-foreground inline-flex items-center gap-1.5 transition-colors"
							>
								<Icon name="rss" size="sm" />
								RSS Feed
							</a>
						</div>
					</div>
				</footer>
			</div>
			<EpicToaster closeButton position="bottom-right" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function Logo({ imgClassName }: { imgClassName?: string }) {
	const localePath = useLocalizedPath()
	return (
		<Link to={localePath('/')} aria-label="Open Sourced">
			<img
				src={logoHorizontalUrl}
				alt="Open Sourced"
				width={825}
				height={192}
				className={imgClassName ?? 'h-8 w-auto dark:brightness-90 dark:invert'}
			/>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
